class SnapCarousel extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.viewport = this.querySelector('[data-carousel-viewport]');
    this.previous = this.querySelector('[data-carousel-previous]');
    this.next = this.querySelector('[data-carousel-next]');
    this.status = this.querySelector('[data-carousel-status]');
    this.slides = [...this.querySelectorAll('[data-carousel-slide]')];
    if (!this.viewport || !this.slides.length) return;
    this.activeIndex = 0;
    this.lastStatus = '';
    this.onPrevious = () => this.goTo(this.activeIndex - 1);
    this.onNext = () => this.goTo(this.activeIndex + 1);
    this.onScroll = () => this.updateStatus();
    this.onKeydown = (event) => {
      const rtl = this.closest('[dir="rtl"]') || document.documentElement.dir === 'rtl';
      const keys = rtl ? { ArrowLeft: 1, ArrowRight: -1 } : { ArrowLeft: -1, ArrowRight: 1 };
      if (keys[event.key] !== undefined) { event.preventDefault(); this.goTo(this.activeIndex + keys[event.key]); }
      if (event.key === 'Home') { event.preventDefault(); this.goTo(0); }
      if (event.key === 'End') { event.preventDefault(); this.goTo(this.slides.length - 1); }
    };
    this.onPointerDown = (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      // Do not capture on pointerdown. A tap on a product or occasion card must
      // reach its anchor; capture only after real horizontal movement starts.
      this.suppressClick = false;
      this.drag = { pointerId: event.pointerId, startX: event.clientX, x: event.clientX, moved: false, captured: false };
    };
    this.onPointerMove = (event) => {
      if (!this.drag || event.pointerId !== this.drag.pointerId) return;
      if (!this.drag.captured && Math.abs(event.clientX - this.drag.startX) < 6) return;
      if (!this.drag.captured) {
        this.drag.captured = true;
        this.viewport.setPointerCapture?.(event.pointerId);
        this.viewport.classList.add('is-dragging');
      }
      const distance = this.drag.x - event.clientX;
      this.drag.x = event.clientX;
      this.drag.moved = true;
      this.viewport.scrollBy?.({ left: distance, behavior: 'auto' });
    };
    this.onPointerEnd = (event) => {
      if (!this.drag || event.pointerId !== this.drag.pointerId) return;
      if (this.drag.captured) {
        this.viewport.releasePointerCapture?.(event.pointerId);
        this.viewport.classList.remove('is-dragging');
      }
      if (event.type === 'pointerup' && this.drag.moved) this.suppressClick = true;
      this.drag = null;
      this.updateStatus();
    };
    this.onClick = (event) => {
      if (!this.suppressClick) return;
      this.suppressClick = false;
      event.preventDefault();
      event.stopPropagation();
    };
    this.previous?.addEventListener('click', this.onPrevious);
    this.next?.addEventListener('click', this.onNext);
    this.viewport.addEventListener('scroll', this.onScroll, { passive: true });
    this.viewport.addEventListener('keydown', this.onKeydown);
    this.viewport.addEventListener('pointerdown', this.onPointerDown);
    this.viewport.addEventListener('pointermove', this.onPointerMove);
    this.viewport.addEventListener('pointerup', this.onPointerEnd);
    this.viewport.addEventListener('pointercancel', this.onPointerEnd);
    this.viewport.addEventListener('click', this.onClick);
    this.resizeObserver = new ResizeObserver(() => this.updateStatus());
    this.resizeObserver.observe(this.viewport);
    this.renderState();
  }
  disconnectedCallback() {
    this.previous?.removeEventListener('click', this.onPrevious); this.next?.removeEventListener('click', this.onNext);
    this.viewport?.removeEventListener('scroll', this.onScroll); this.viewport?.removeEventListener('keydown', this.onKeydown);
    this.viewport?.removeEventListener('pointerdown', this.onPointerDown); this.viewport?.removeEventListener('pointermove', this.onPointerMove);
    this.viewport?.removeEventListener('pointerup', this.onPointerEnd); this.viewport?.removeEventListener('pointercancel', this.onPointerEnd);
    this.viewport?.removeEventListener('click', this.onClick);
    this.resizeObserver?.disconnect(); this.initialized = false;
  }
  behavior() { return document.documentElement.classList.contains('motion-reduced') || window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'; }
  goTo(index) {
    this.activeIndex = Math.max(0, Math.min(index, this.slides.length - 1));
    this.slides[this.activeIndex].scrollIntoView({ block: 'nearest', inline: 'start', behavior: this.behavior() });
    this.renderState();
  }
  updateStatus() {
    const bounds = this.viewport.getBoundingClientRect();
    const rects = this.slides.map((slide) => slide.getBoundingClientRect());
    const epsilon = 1;
    let active = this.activeIndex;
    const first = rects[0];
    const last = rects.at(-1);
    if (first.left >= bounds.left - epsilon && first.right <= bounds.right + epsilon) active = 0;
    else if (last.left >= bounds.left - epsilon && last.right <= bounds.right + epsilon) active = this.slides.length - 1;
    else {
      const visible = rects.findIndex((rect) => rect.right > bounds.left + epsilon && rect.left < bounds.right - epsilon);
      if (visible !== -1) active = visible;
    }
    this.activeIndex = active;
    this.renderState();
  }
  updateAlignment() {
    const centered = this.viewport.scrollWidth <= this.viewport.clientWidth + 1;
    this.viewport.classList.toggle('is-centered', centered);
  }
  renderState() {
    this.updateAlignment();
    const active = this.activeIndex;
    const label = String(active + 1) + ' / ' + String(this.slides.length);
    if (this.status && this.lastStatus !== label) { this.status.textContent = label; this.lastStatus = label; }
    if (this.previous) this.previous.disabled = active === 0;
    if (this.next) this.next.disabled = active === this.slides.length - 1;
  }
}
if (!customElements.get('snap-carousel')) customElements.define('snap-carousel', SnapCarousel);
